// components/PassengerPicker.js
import { on } from '../utils/dom.js';

export class PassengerPicker {
  constructor(root) {
    this.root             = root;
    this.wrap             = this.root.querySelector('.nalu-picker-col--passengers'); // scope
    this.summaryEl        = this.wrap?.querySelector('.nalu-picker-text') || this.root.querySelector('.nalu-picker-text');
    this.onChange         = null;
    this._inited          = false;
    this._debounceTimer   = null;
    this._debounceMs      = 350; // tweak 250–400ms to taste
  }

  // Resolve current scope every time (the DOM may be re-rendered)
  _scope() {
    return this.root.querySelector('.nalu-picker-col--passengers') || this.root;
  }
  
  _summaryEl() {
    return this._scope().querySelector('.nalu-picker-text') || this.root.querySelector('.nalu-picker-text');
  }

  init() {
    if (this._inited) return;
    this._inited = true;
    //console.log('PassengerPicker init called');

    // If your +/- buttons accidentally contain numbers, normalize them once
    this._scope().querySelectorAll('.nalu-picker-minus').forEach(b => {
      if (!b.dataset.origLabel) { b.dataset.origLabel = b.textContent.trim(); }
      // Typical is "–" or "-" – keep a symbol; if it’s a number, replace
      if (/^\d+$/.test(b.textContent.trim())) b.textContent = '–';
    });
    this._scope().querySelectorAll('.nalu-picker-plus').forEach(b => {
      if (!b.dataset.origLabel) { b.dataset.origLabel = b.textContent.trim(); }
      if (/^\d+$/.test(b.textContent.trim())) b.textContent = '+';
    });

    /* make sure Adults starts at 1 (the min) */
    //this.set(1, 0, { emit: true });

    /* delegated +/- clicks */
    on(this._scope(), 'click', e => {
      const btn = e.target.closest('.nalu-picker-plus, .nalu-picker-minus');
      if (!btn) return;

      // Which counter do we change? Prefer explicit data-counter on the button;
      // otherwise, look for the sibling display.
      let counter = btn.dataset.counter;
      if (!counter) {
        const row = btn.closest('.nalu-picker-dropdown__row') || this._scope();
        const display = row.querySelector('.nalu-picker-count[data-counter]');
        counter = display?.dataset.counter;
      }
      if (!counter) return;

      const delta  = btn.classList.contains('nalu-picker-plus') ? 1 : -1;
      const cur    = this.get(counter);
      const min    = counter === 'adults' ? 1 : 0;
      const newVal = Math.max(min, cur + delta);

      this.setCounter(counter, newVal);
      this.updateSummary();
      this._emitChange();
    });

    /* manual edits in the displays */
    this._scope().querySelectorAll('.nalu-picker-count').forEach(el =>
      on(el, 'input', () => {
        const counter = el.dataset.counter;
        const min     = counter === 'adults' ? 1 : 0;
        const n       = Math.max(min, Number(el.textContent) || 0);
        el.textContent = n;
        this.updateSummary();
        this._emitChange();
      })
    );

    // Close-hook (if you use a dropdown close event)
    document.addEventListener('nalu:passengers:closed', () => {
      this.updateSummary();
      this._emitChange(true);
      //this._emitChange();
    });
  }

  // Programmatic setter
  set(adults = 1, children = 0, { emit = false } = {}) {
    //console.log('PassengerPicker set called with', adults, children);
    const a = Math.max(1, Number(adults) || 0);
    const c = Math.max(0, Number(children) || 0);
    this.setCounter('adults', a);
    this.setCounter('children', c);
    this.updateSummary();
    if (emit) this._emitChange();
  }

  /* ---------- helpers ---------- */
  _emitChange(immediate = false) {
    // Fire immediately when the dropdown closes, otherwise debounce.
    if (immediate) {
      clearTimeout(this._debounceTimer);
      this.onChange?.(this.adults, this.children);
      return;
    }
    clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(() => {
      this.onChange?.(this.adults, this.children);
    }, this._debounceMs);
  }
  //_emitChange() { this.onChange?.(this.adults, this.children); }

  /** read only from the count display (not buttons) */
  get(counter) {
    const scope   = this._scope();
    const display = scope.querySelector(`.nalu-picker-count[data-counter="${counter}"]`);
    return Number(display?.textContent) || 0;
  }

  /** write only to the count displays */
  setCounter(counter, valueOrMapper) {
    const scope = this._scope();
    const els   = scope.querySelectorAll(`.nalu-picker-count[data-counter="${counter}"]`);
    els.forEach(el => {
      const next = (typeof valueOrMapper === 'function')
        ? valueOrMapper(Number(el.textContent) || 0)
        : valueOrMapper;
      el.textContent = next;
    });
  }

  resetToDefault() { this.set(1, 0, { emit: true }); }

  get adults()   { return this.get('adults')   || 1; }
  get children() { return this.get('children') || 0; }

  updateSummary() {
    const a = this.adults;
    const c = this.children;
    const parts = [];
    if (a) parts.push(`${a} Adult${a > 1 ? 's' : ''}`);
    if (c) parts.push(`${c} Child${c > 1 ? 'ren' : ''}`);
    const sum = this._summaryEl();
    if (sum) sum.textContent = parts.join(', ');
    //this.summaryEl.textContent = parts.join(', ');
  }
}
